import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { axiosInstanceNewAPI } from '@/services/api/utils/axiosInstance';
import { apiPath } from '@/redux/apiPath';
import { annotationSchema } from '@/models/annotationSchema';
import { pageableSchema } from '@/models/pageableSchema';
import { PageOf } from '@/types/models';
import { validateDataUsingZodSchema } from '@/utils/validateDataUsingZodSchema';
import { extractAxiosErrorMessage } from '@/utils/getErrorMessage/getErrorMessage.utils';
import { MAP_ANNOTATIONS_SLICE_NAME } from './mapAnnotations.constants';
import {
  AddMapAnnotationPayload,
  DeleteMapAnnotationPayload,
  GetMapAnnotationsPayload,
  MapAnnotation,
  UpdateMapAnnotationPayload,
} from './mapAnnotations.types';

function toRejectValue(error: unknown, fallbackMessage: string): { name: string; message: string } {
  if (axios.isAxiosError(error)) {
    return { name: error.name, message: extractAxiosErrorMessage(error) };
  }
  if (error instanceof Error) {
    return { name: error.name, message: error.message };
  }
  return { name: 'Unknown Error', message: fallbackMessage };
}

export const getMapAnnotations = createAsyncThunk(
  `${MAP_ANNOTATIONS_SLICE_NAME}/getMapAnnotations`,
  async ({ projectId, mapId }: GetMapAnnotationsPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.get<PageOf<MapAnnotation>>(
        apiPath.getMapAnnotations(projectId, mapId),
      );

      const isDataValid = validateDataUsingZodSchema(
        response.data,
        pageableSchema(annotationSchema),
      );

      return isDataValid
        ? { mapId, annotations: response.data.content }
        : rejectWithValue({ message: 'Failed to fetch map annotations' });
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to fetch map annotations'));
    }
  },
);

export const addMapAnnotation = createAsyncThunk(
  `${MAP_ANNOTATIONS_SLICE_NAME}/addMapAnnotation`,
  async ({ projectId, mapId, annotationData }: AddMapAnnotationPayload, { rejectWithValue }) => {
    try {
      const response = await axiosInstanceNewAPI.post<MapAnnotation>(
        apiPath.addMapAnnotation(projectId, mapId),
        annotationData,
      );

      const isDataValid = validateDataUsingZodSchema(response.data, annotationSchema);

      return isDataValid
        ? { mapId, annotation: response.data }
        : rejectWithValue({ message: 'Failed to add map annotation' });
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to add map annotation'));
    }
  },
);

export const updateMapAnnotation = createAsyncThunk(
  `${MAP_ANNOTATIONS_SLICE_NAME}/updateMapAnnotation`,
  async (
    { projectId, mapId, annotationId, annotationData }: UpdateMapAnnotationPayload,
    { rejectWithValue },
  ) => {
    try {
      const response = await axiosInstanceNewAPI.put<MapAnnotation>(
        apiPath.updateMapAnnotation(projectId, mapId, annotationId),
        annotationData,
      );

      const isDataValid = validateDataUsingZodSchema(response.data, annotationSchema);

      return isDataValid
        ? { mapId, annotation: response.data }
        : rejectWithValue({ message: 'Failed to update map annotation' });
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to update map annotation'));
    }
  },
);

export const deleteMapAnnotation = createAsyncThunk(
  `${MAP_ANNOTATIONS_SLICE_NAME}/deleteMapAnnotation`,
  async ({ projectId, mapId, annotationId }: DeleteMapAnnotationPayload, { rejectWithValue }) => {
    try {
      await axiosInstanceNewAPI.delete(apiPath.deleteMapAnnotation(projectId, mapId, annotationId));

      return { mapId, annotationId };
    } catch (error: unknown) {
      return rejectWithValue(toRejectValue(error, 'Failed to delete map annotation'));
    }
  },
);
